import React, { useState } from 'react';
import { X, Minus, Plus, Trash2, MapPin, Truck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQuantity, removeItem, clearCart, subtotal, deliveryZones, saveOrder, generateWhatsAppMessage } = useCart();
  const { user, isB2B } = useAuth();
  const [step, setStep] = useState('cart');
  const [saving, setSaving] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: '',
    deliveryType: 'pickup',
    zone: 'Chonchi',
    address: '',
    notes: ''
  });

  const getDeliveryCost = () => {
    if (customerInfo.deliveryType === 'pickup') return 0;
    if (subtotal >= 15000) return 0; // Free shipping over 15k
    const zone = deliveryZones.find(z => z.name === customerInfo.zone);
    return zone?.delivery_cost || 0;
  };

  const deliveryCost = getDeliveryCost();
  const orderTotal = subtotal + deliveryCost;

  const handleCheckout = () => {
    if (items.length === 0) return;
    setStep('checkout');
  };

  const handleSendWhatsApp = async () => {
    if (!customerInfo.name || !customerInfo.phone) {
      toast.error('Por favor completa tu nombre y teléfono');
      return;
    }
    if (customerInfo.deliveryType === 'delivery' && !customerInfo.address) {
      toast.error('Por favor ingresa tu dirección');
      return;
    }

    setSaving(true);
    try {
      // Save order to Supabase
      await saveOrder({
        ...customerInfo,
        b2bUserId: isB2B ? user?.id : null
      }, deliveryCost);

      // Open WhatsApp
      const message = generateWhatsAppMessage(customerInfo, deliveryCost);
      const whatsappUrl = `https://wa.me/56994798582?text=${message}`;
      window.open(whatsappUrl, '_blank');

      toast.success('Pedido enviado correctamente');
      clearCart();
      setIsOpen(false);
      setStep('cart');
      setCustomerInfo({ name: '', phone: '', email: '', deliveryType: 'pickup', zone: 'Chonchi', address: '', notes: '' });
    } catch (error) {
      console.error('Error saving order:', error);
      // Even if DB save fails, still send WhatsApp
      const message = generateWhatsAppMessage(customerInfo, deliveryCost);
      const whatsappUrl = `https://wa.me/56994798582?text=${message}`;
      window.open(whatsappUrl, '_blank');
      clearCart();
      setIsOpen(false);
      setStep('cart');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => { setIsOpen(false); setStep('cart'); }}
            className="fixed inset-0 bg-black/50 z-50"
          />

          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[#E8EAE6]">
              <h2 className="font-cabinet font-bold text-xl">
                {step === 'cart' ? 'Tu Carrito' : 'Finalizar Pedido'}
              </h2>
              <button
                onClick={() => { setIsOpen(false); setStep('cart'); }}
                className="p-2 hover:bg-[#F3F3EF] rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {step === 'cart' ? (
                <>
                  {items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center p-8">
                      <div className="w-20 h-20 bg-[#F3F3EF] rounded-full flex items-center justify-center mb-4">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#808E83" strokeWidth="1.5">
                          <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                          <line x1="3" y1="6" x2="21" y2="6"/>
                          <path d="M16 10a4 4 0 0 1-8 0"/>
                        </svg>
                      </div>
                      <h3 className="font-cabinet font-semibold text-lg mb-2">Tu carrito está vacío</h3>
                      <p className="text-[#808E83] text-sm">Agrega productos para comenzar tu pedido</p>
                    </div>
                  ) : (
                    <div className="p-4 space-y-4">
                      {items.map(item => (
                        <div key={item.product.id} className="flex gap-3 p-3 bg-[#F3F3EF] rounded-xl">
                          <div className="w-20 h-20 bg-white rounded-lg overflow-hidden flex-shrink-0">
                            {item.product.image ? (
                              <img src={item.product.image} alt={item.product.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-2xl">🥬</div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm truncate">{item.product.name}</h4>
                            <p className="text-xs text-[#808E83]">{item.product.unit}</p>
                            <p className="font-semibold text-[#2C5530] mt-1">${item.product.price.toLocaleString('es-CL')}</p>

                            <div className="flex items-center justify-between mt-2">
                              <div className="flex items-center gap-2 bg-white rounded-lg p-1">
                                <button
                                  onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                                  className="w-7 h-7 flex items-center justify-center hover:bg-[#F3F3EF] rounded"
                                >
                                  <Minus size={14} />
                                </button>
                                <span className="w-6 text-center font-medium text-sm">{item.quantity}</span>
                                <button
                                  onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                                  className="w-7 h-7 flex items-center justify-center hover:bg-[#F3F3EF] rounded"
                                >
                                  <Plus size={14} />
                                </button>
                              </div>
                              <button
                                onClick={() => removeItem(item.product.id)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="p-4 space-y-4">
                  {/* Customer Info */}
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-[#4A5D4E] mb-1">Nombre *</label>
                      <input
                        type="text"
                        value={customerInfo.name}
                        onChange={e => setCustomerInfo({...customerInfo, name: e.target.value})}
                        className="w-full px-4 py-3 border border-[#E8EAE6] rounded-xl focus:outline-none focus:border-[#2C5530]"
                        placeholder="Tu nombre"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#4A5D4E] mb-1">Teléfono *</label>
                      <input
                        type="tel"
                        value={customerInfo.phone}
                        onChange={e => setCustomerInfo({...customerInfo, phone: e.target.value})}
                        className="w-full px-4 py-3 border border-[#E8EAE6] rounded-xl focus:outline-none focus:border-[#2C5530]"
                        placeholder="+56 9 1234 5678"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#4A5D4E] mb-1">Email (opcional)</label>
                      <input
                        type="email"
                        value={customerInfo.email}
                        onChange={e => setCustomerInfo({...customerInfo, email: e.target.value})}
                        className="w-full px-4 py-3 border border-[#E8EAE6] rounded-xl focus:outline-none focus:border-[#2C5530]"
                        placeholder="tu@email.com"
                      />
                    </div>
                  </div>

                  {/* Delivery Type */}
                  <div>
                    <label className="block text-xs font-medium text-[#4A5D4E] mb-2">Tipo de entrega</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setCustomerInfo({...customerInfo, deliveryType: 'pickup'})}
                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border transition-colors ${
                          customerInfo.deliveryType === 'pickup'
                            ? 'border-[#2C5530] bg-[#2C5530]/5 text-[#2C5530]'
                            : 'border-[#E8EAE6] hover:border-[#2C5530]/30'
                        }`}
                      >
                        <MapPin size={18} />
                        <span className="font-medium text-sm">Retiro en local</span>
                      </button>
                      <button
                        onClick={() => setCustomerInfo({...customerInfo, deliveryType: 'delivery'})}
                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border transition-colors ${
                          customerInfo.deliveryType === 'delivery'
                            ? 'border-[#2C5530] bg-[#2C5530]/5 text-[#2C5530]'
                            : 'border-[#E8EAE6] hover:border-[#2C5530]/30'
                        }`}
                      >
                        <Truck size={18} />
                        <span className="font-medium text-sm">A domicilio</span>
                      </button>
                    </div>
                  </div>

                  {/* Delivery Zone & Address */}
                  {customerInfo.deliveryType === 'delivery' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-[#4A5D4E] mb-1">Zona de reparto</label>
                        <select
                          value={customerInfo.zone}
                          onChange={e => setCustomerInfo({...customerInfo, zone: e.target.value})}
                          className="w-full px-4 py-3 border border-[#E8EAE6] rounded-xl focus:outline-none focus:border-[#2C5530] bg-white"
                        >
                          {deliveryZones.filter(z => z.name !== 'Chonchi').map(zone => (
                            <option key={zone.name} value={zone.name}>
                              {zone.name} - {zone.delivery_day} (Envío: ${zone.delivery_cost.toLocaleString('es-CL')})
                            </option>
                          ))}
                        </select>
                        {subtotal >= 15000 && (
                          <p className="text-xs text-[#25D366] mt-1 font-medium">✓ Envío GRATIS por compra sobre $15.000</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#4A5D4E] mb-1">Dirección *</label>
                        <input
                          type="text"
                          value={customerInfo.address}
                          onChange={e => setCustomerInfo({...customerInfo, address: e.target.value})}
                          className="w-full px-4 py-3 border border-[#E8EAE6] rounded-xl focus:outline-none focus:border-[#2C5530]"
                          placeholder="Tu dirección completa"
                        />
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  <div>
                    <label className="block text-xs font-medium text-[#4A5D4E] mb-1">Notas (opcional)</label>
                    <textarea
                      value={customerInfo.notes}
                      onChange={e => setCustomerInfo({...customerInfo, notes: e.target.value})}
                      className="w-full px-4 py-3 border border-[#E8EAE6] rounded-xl focus:outline-none focus:border-[#2C5530] resize-none"
                      rows={2}
                      placeholder="Instrucciones especiales..."
                    />
                  </div>

                  {/* Order Summary */}
                  <div className="bg-[#F3F3EF] rounded-xl p-4">
                    <h4 className="font-medium text-sm mb-2">Resumen del pedido</h4>
                    <div className="space-y-1 text-sm">
                      {items.map(item => (
                        <div key={item.product.id} className="flex justify-between text-[#4A5D4E]">
                          <span>{item.product.name} x{item.quantity}</span>
                          <span>${(item.product.price * item.quantity).toLocaleString('es-CL')}</span>
                        </div>
                      ))}
                      {deliveryCost > 0 && (
                        <div className="flex justify-between text-[#4A5D4E] pt-2 border-t border-[#E8EAE6] mt-2">
                          <span>Envío ({customerInfo.zone})</span>
                          <span>${deliveryCost.toLocaleString('es-CL')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="p-4 border-t border-[#E8EAE6] space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Total</span>
                  <span className="font-cabinet font-bold text-xl text-[#2C5530]">${orderTotal.toLocaleString('es-CL')}</span>
                </div>

                {subtotal >= 15000 && customerInfo.deliveryType === 'delivery' && (
                  <div className="text-center text-sm text-[#25D366] font-medium">
                    ✓ Envío GRATIS incluido
                  </div>
                )}

                {step === 'cart' ? (
                  <button
                    onClick={handleCheckout}
                    className="w-full bg-[#2C5530] hover:bg-[#3A6E40] text-white font-medium py-3 px-6 rounded-full transition-colors"
                  >
                    Continuar
                  </button>
                ) : (
                  <div className="space-y-2">
                    <button
                      onClick={handleSendWhatsApp}
                      disabled={saving}
                      className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] disabled:bg-gray-400 text-white font-bold py-3 px-6 rounded-full transition-colors"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      {saving ? 'Procesando...' : 'Enviar pedido por WhatsApp'}
                    </button>
                    <button
                      onClick={() => setStep('cart')}
                      className="w-full text-[#4A5D4E] font-medium py-2 hover:text-[#2C5530] transition-colors"
                    >
                      ← Volver al carrito
                    </button>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
